import React from "react";
import GaugeComponent from "react-gauge-component";

const OverallPerformanceGauge = ({ score, label = "Overall Health Score" }) => {
  // Performance stages with ranges and colors
  const performanceStages = [
    { name: "VERY POOR", min: 0, max: 20, color: "#ef5452" },
    { name: "POOR", min: 20, max: 40, color: "#F38B06" },
    { name: "AVERAGE", min: 40, max: 60, color: "#e1cc00" },
    { name: "GOOD", min: 60, max: 75, color: "#92B83C" },
    { name: "VERY GOOD", min: 75, max: 90, color: "#459d55" },
    { name: "EXCELLENT", min: 90, max: 100, color: "#10b981" },
  ];

  // Determine current performance stage
  const getCurrentStage = (score) => {
    return (
      performanceStages.find(
        (stage) => score >= stage.min && score < stage.max
      ) || performanceStages[performanceStages.length - 1]
    );
  };

  const currentStage = getCurrentStage(score);

  // Get status message with emoji
  const getStatusMessage = (stageName) => {
    const emojiMap = {
      "VERY POOR": "🚨",
      POOR: "⚠️",
      AVERAGE: "😐",
      GOOD: "👍",
      "VERY GOOD": "💪",
      EXCELLENT: "🎉",
    };
    return emojiMap[stageName] || "📊";
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-2 text-center">
        {label}
      </h3>

      {/* Gauge Component */}
      <div className="flex justify-center">
        <GaugeComponent
          type="semicircle"
          arc={{
            width: 0.2,
            padding: 0.005,
            cornerRadius: 1,
            subArcs: [
              {
                limit: 20,
                color: "#ef5452",
                showTick: true,
              },
              {
                limit: 40,
                color: "#F38B06",
                showTick: true,
              },
              {
                limit: 60,
                color: "#e1cc00",
                showTick: true,
              },
              {
                limit: 75,
                color: "#92B83C",
                showTick: true,
              },
              {
                limit: 90,
                color: "#459d55",
                showTick: true,
              },
              {
                limit: 100,
                color: "#10b981",
                showTick: true,
              },
            ],
          }}
          pointer={{
            color: "#1f2937",
            length: 0.8,
            width: 15,
            elastic: true,
          }}
          labels={{
            valueLabel: {
              formatTextValue: (value) => `${value}`,
              fontSize: 48,
              style: { fill: currentStage.color, fontWeight: "bold" },
            },
            tickLabels: {
              type: "outer",
              ticks: [
                { value: 0 },
                { value: 20 },
                { value: 40 },
                { value: 60 },
                { value: 75 },
                { value: 90 },
                { value: 100 },
              ],
              defaultTickValueConfig: {
                formatTextValue: (value) => value.toString(),
                style: { fontSize: 10, fill: "#666" },
              },
            },
          }}
          value={score}
          minValue={0}
          maxValue={100}
        />
      </div>

      {/* "out of 100" text */}
      <div className="text-center -mt-8 mb-4">
        <span className="text-sm text-gray-500">out of 100</span>
      </div>

      {/* Status Message */}
      <div className="mt-4 text-center">
        <div
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full"
          style={{ backgroundColor: `${currentStage.color}20` }}
        >
          <span className="text-2xl">
            {getStatusMessage(currentStage.name)}
          </span>
          <span
            className="text-sm font-medium"
            style={{ color: currentStage.color }}
          >
            {currentStage.name}
          </span>
        </div>
      </div>

      {/* Performance Scale Legend */}
      <div className="mt-6 pt-4 border-t border-gray-200">
        <h4 className="text-xs font-semibold text-gray-700 mb-3 text-center">
          Performance Scale
        </h4>
        <div className="grid grid-cols-3 gap-2 text-xs">
          {performanceStages.map((stage, index) => (
            <div key={index} className="text-center">
              <div
                className="w-4 h-4 rounded-full mx-auto mb-1"
                style={{ backgroundColor: stage.color }}
              ></div>
              <div className="text-gray-600 font-medium">
                {stage.min}-{stage.max}
              </div>
              <div className="text-gray-500 text-[10px]">{stage.name}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default OverallPerformanceGauge;
